// Call plugin.
import { callActions as actions } from '../actions'
import { getCalls, getCallById, getOptions } from '../selectors'
import { normalizeSipUri } from '../../utils/normalization'

// Other plugins.
import { getUserInfo, getDomain } from '../../../auth/interface/selectors'
import { setSdpHandlers } from '../../../config/interface/actions'

// Libraries.
import { v4 as uuidV4 } from 'uuid'
import { isEmpty } from 'lodash/fp'
import { defer } from '../../../common/utils'

import { CALL_STATES, CALL_MEDIA_CONNECTION_STATES } from '../../constants'

import { logManager, API_LOG_TAG } from '../../../logs'

const log = logManager.getLogger('CALL')

export default function callAPI ({ dispatch, getState }) {
  return {
    /**
     * Starts an outgoing call to a {@link call.UserID UserID} or a
     *    {@link call.PhoneNumber PhoneNumber}.
     *
     * The call will be tracked by a unique ID that is returned by the API. The
     *    application will use this ID to identify and control the call after it
     *    has been initiated.
     *
     * The {@link call.getById} API can be used to retrieve the
     *    current information about the call.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:start call:start} event
     *    locally when the operation completes. When the remote participant
     *    receives the call, a {@link call.event:call:receive call:receive}
     *    event will be emitted remotely for them.
     *
     * The SDK requires access to the machine's media devices (e.g. microphone)
     *    in order to make a call. If it does not already have permissions to
     *    use the devices, the user may be prompted by the browser to give
     *    permissions.
     * @public
     * @static
     * @memberof call
     * @requires cpaas_call
     * @method make
     * @param {call.UserID|call.PhoneNumber} destination The desired destination.
     * @param {Object} media The media options the call should be initialized with.
     * @param {boolean} [media.audio=false] Whether the call should have audio on start. Currently, audio-less calls are not supported.
     * @param {Object} [media.audioOptions] Options for configuring the call's audio.
     * @param {call.MediaConstraint} [media.audioOptions.deviceId] ID of the microphone to receive audio from.
     * @param {boolean} [media.video=false] Whether the call should have video on start.
     * @param {Object} [media.videoOptions] Options for configuring the call's video.
     * @param {call.MediaConstraint} [media.videoOptions.deviceId] ID of the camera to receive video from.
     * @param {call.MediaConstraint} [media.videoOptions.height] The height of the video.
     * @param {call.MediaConstraint} [media.videoOptions.width] The width of the video.
     * @param {call.MediaConstraint} [media.videoOptions.frameRate] The frame rate of the video.
     * @param {boolean} [media.screen=false] Whether the call should have screenshare on start. (Note: Screensharing is not supported on iOS Safari.)
     * @param {Object} [media.screenOptions] Options for configuring the call's screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.height] The height of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.width] The width of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.frameRate] The frame rate of the screenShare.
     * @param {Object} [options]
     * @param {call.BandwidthControls} [options.bandwidth] Options for configuring media's bandwidth.
     * @param {call.DSCPControls} [options.dscpControls] Options for configuring DSCP markings on the media traffic
     * @returns {string} The generated ID of the newly created call.
     * @example
     * // Listen for the event emitted after making a call.
     * client.on('call:start', function (params) {
     *   const { callId, error } = params
     *   if (error) {
     *     // Call failed to initialize.
     *   } else {
     *     // Call was initialized, and the recipient user will be notified.
     *   }
     * })
     * // Make an audio-only call.
     * const newCallId = client.call.make(destination, { audio: true })
     */

    /**
     * Starts an outgoing call to a {@link call.SIP_URI SIP_URI} or a
     *    {@link call.TEL_URI TEL_URI}.
     *
     * The call will be tracked by a unique ID that is returned by the API. The
     *    application will use this ID to identify and control the call after it
     *    has been initiated.
     *
     * The {@link call.getById} API can be used to retrieve
     *    the current information about the call.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:start call:start} event
     *    locally when the operation completes. When the remote participant
     *    receives the call, a {@link call.event:call:receive call:receive}
     *    event will be emitted remotely for them.
     *
     * The SDK requires access to the machine's media devices (eg. microphone)
     *    in order to make a call. If it does not already have permissions to
     *    use the devices, the user may be prompted by the browser to give
     *    permissions.
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @method make
     * @param {call.SIP_URI|call.TEL_URI} destination The desired destination.
     * @param {Object} media The media options the call should be initialized with.
     * @param {boolean} [media.audio=false] Whether the call should have audio on start. Currently, audio-less calls are not supported.
     * @param {Object} [media.audioOptions] Options for configuring the call's audio.
     * @param {call.MediaConstraint} [media.audioOptions.deviceId] ID of the microphone to receive audio from.
     * @param {boolean} [media.video=false] Whether the call should have video on start.
     * @param {Object} [media.videoOptions] Options for configuring the call's video.
     * @param {call.MediaConstraint} [media.videoOptions.deviceId] ID of the camera to receive video from.
     * @param {call.MediaConstraint} [media.videoOptions.height] The height of the video.
     * @param {call.MediaConstraint} [media.videoOptions.width] The width of the video.
     * @param {call.MediaConstraint} [media.videoOptions.frameRate] The frame rate of the video.
     * @param {boolean} [media.screen=false] Whether the call should have screenshare on start. (Note: Screensharing is not supported on iOS Safari.)
     * @param {Object} [media.screenOptions] Options for configuring the call's screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.height] The height of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.width] The width of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.frameRate] The frame rate of the screenShare.
     * @param {Object} [options]
     * @param {call.BandwidthControls} [options.bandwidth] Options for configuring media's bandwidth.
     * @param {string} [options.displayName] Custom display name to be provided to the destination. Not supported in all environments and may use default display name.
     * @param {Array<call.CustomParameter>} [options.customParameters] Custom SIP header parameters for the SIP backend.
     * @param {call.DSCPControls} [options.dscpControls] Options for configuring DSCP markings on the media traffic
     * @returns {string} The generated ID of the newly created call.
     * @example
     * // Listen for the event emitted after making a call.
     * client.on('call:start', function (params) {
     *   const { callId, error } = params
     *   if (error) {
     *     // Call failed to initialize.
     *   } else {
     *     // Call was initialized, and the recipient user will be notified.
     *   }
     * })
     * // Make an audio-only call.
     * const newCallId = client.call.make(destination, { audio: true })
     */

    /**
     * @private
     * @static
     * @memberof call
     * @requires link_call
     * @requires callMe
     * @method make
     * @param {Object} [options]
     * @param {Array<call.CustomBody>} [options.customBodies] Custom SIP bodies for the SIP backend.
     */
    make (destination, media, options = {}) {
      log.debug(API_LOG_TAG + 'call.make: ', destination, media, options)

      const callId = uuidV4()
      const from = getUserInfo(getState()).username

      const mediaConstraints = {
        audio: media.audio && !isEmpty(media.audioOptions) ? media.audioOptions : media.audio,
        video: media.video && !isEmpty(media.videoOptions) ? media.videoOptions : media.video,
        screenShare: media.screen && !isEmpty(media.screenOptions) ? media.screenOptions : media.screen
      }

      const config = getOptions(getState())
      if (config.normalizeDestination) {
        // Normalize callee addresses
        destination = normalizeSipUri(destination, getDomain(getState()))
        log.info('Address normalized to: ', destination)
      }

      dispatch(
        actions.makeCall(callId, {
          participantAddress: destination,
          // TODO: Have better property names.
          from,
          mediaConstraints,
          ...options
        })
      )
      return callId
    },

    /**
     * Rejects an incoming call.
     *
     * The specified call to reject must be in a ringing state with an incoming
     *    direction. The call will be ended as a result of the operation.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. The remote participant
     *    will be notified, through their own
     *    {@link call.event:call:stateChange call:stateChange} event, that the
     *    call was rejected.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @method reject
     * @param {string} callId The ID of the call to reject.
     */
    reject (callId) {
      log.debug(API_LOG_TAG + 'call.reject: ', callId)
      dispatch(actions.rejectCall(callId))
    },

    /**
     * Answers an incoming call.
     *
     * The specified call to answer must be in Initiated or Ringing state with an incoming
     *    direction. The call will become connected as a result of the operation.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. This indicates that the
     *    call has connected with the remote participant. The
     *    {@link call.getById} API can be used to retrieve the latest call state
     *    after the change. Further events will be emitted to indicate that the
     *    call has received media from the remote participant. See the
     *    {@link call.event:call:newTrack call:newTrack} event for more
     *    information about this.
     *
     * The SDK requires access to the system's media devices (eg. microphone)
     *    in order to answer a call. If it does not already have permissions to
     *    use the devices, the user may be prompted by the browser to give
     *    permissions.
     * @public
     * @static
     * @memberof call
     * @requires cpaas_call
     * @method answer
     * @param {string} callId The ID of the call to answer.
     * @param {Object} media The media options the call should be initialized with.
     * @param {boolean} [media.audio=false] Whether the call should have audio on start. Currently, audio-less calls are not supported.
     * @param {Object} [media.audioOptions] Options for configuring the call's audio.
     * @param {call.MediaConstraint} [media.audioOptions.deviceId] ID of the microphone to receive audio from.
     * @param {boolean} [media.video=false] Whether the call should have video on start.
     * @param {Object} [media.videoOptions] Options for configuring the call's video.
     * @param {call.MediaConstraint} [media.videoOptions.deviceId] ID of the camera to receive video from.
     * @param {call.MediaConstraint} [media.videoOptions.height] The height of the video.
     * @param {call.MediaConstraint} [media.videoOptions.width] The width of the video.
     * @param {call.MediaConstraint} [media.videoOptions.frameRate] The frame rate of the video.
     * @param {boolean} [media.screen=false] Whether the call should have screenshare on start. (Note: Screensharing is not supported on iOS Safari.)
     * @param {Object} [media.screenOptions] Options for configuring the call's screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.height] The height of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.width] The width of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.frameRate] The frame rate of the screenShare.
     * @param {Object} [options]
     * @param {call.BandwidthControls} [options.bandwidth] Options for configuring media's bandwidth.
     * @param {call.DSCPControls} [options.dscpControls] Options for configuring DSCP markings on the media traffic
     */

    /**
     * Answers an incoming call.
     *
     * The specified call to answer must be in a ringing state with an incoming
     *    direction. The call will become connected as a result of the operation.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. This indicates that the
     *    call has connected with the remote participant. The
     *    {@link call.getById} API can be used to retrieve the latest call state
     *    after the change. Further events will be emitted to indicate that the
     *    call has received media from the remote participant. See the
     *    {@link call.event:call:newTrack call:newTrack} event for more
     *    information about this.
     *
     * The SDK requires access to the system's media devices (eg. microphone)
     *    in order to answer a call. If it does not already have permissions to
     *    use the devices, the user may be prompted by the browser to give
     *    permissions.
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @method answer
     * @param {string} callId The ID of the call to answer.
     * @param {Object} media The media options the call should be initialized with.
     * @param {boolean} [media.audio=false] Whether the call should have audio on start. Currently, audio-less calls are not supported.
     * @param {Object} [media.audioOptions] Options for configuring the call's audio.
     * @param {call.MediaConstraint} [media.audioOptions.deviceId] ID of the microphone to receive audio from.
     * @param {boolean} [media.video=false] Whether the call should have video on start.
     * @param {Object} [media.videoOptions] Options for configuring the call's video.
     * @param {call.MediaConstraint} [media.videoOptions.deviceId] ID of the camera to receive video from.
     * @param {call.MediaConstraint} [media.videoOptions.height] The height of the video.
     * @param {call.MediaConstraint} [media.videoOptions.width] The width of the video.
     * @param {call.MediaConstraint} [media.videoOptions.frameRate] The frame rate of the video.
     * @param {boolean} [media.screen=false] Whether the call should have screenshare on start. (Note: Screensharing is not supported on iOS Safari.)
     * @param {Object} [media.screenOptions] Options for configuring the call's screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.height] The height of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.width] The width of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.frameRate] The frame rate of the screenShare.
     * @param {Object} [options]
     * @param {call.BandwidthControls} [options.bandwidth] Options for configuring media's bandwidth.
     * @param {Array<call.CustomParameter>} [options.customParameters] Custom SIP header parameters for the SIP backend.
     * @param {call.DSCPControls} [options.dscpControls] Options for configuring DSCP markings on the media traffic
     */

    /**
     * @private
     * @static
     * @memberof call
     * @requires link_call
     * @requires callMe
     * @method answer
     * @param {Object} [options]
     * @param {Array<call.CustomBody>} [options.customBodies] Custom SIP bodies for the SIP backend.
     */
    answer (callId, media, options = {}) {
      log.debug(API_LOG_TAG + 'call.answer: ', callId, media, options)

      const mediaConstraints = {
        audio: media.audio && !isEmpty(media.audioOptions) ? media.audioOptions : media.audio,
        video: media.video && !isEmpty(media.videoOptions) ? media.videoOptions : media.video,
        screenShare: media.screen && !isEmpty(media.screenOptions) ? media.screenOptions : media.screen
      }

      dispatch(
        actions.answerCall(callId, {
          mediaConstraints,
          ...options
        })
      )
    },

    /**
     * Sends the "ringing feedback" notification to the remote participant of a call.
     *
     * When using the 'manual' `ringingFeedbackMode` configuration, the application
     *    is responsible for transitioning the call into the `Ringing` state. This
     *    API will notify both ends of the call to enter `Ringing` state at the same
     *    time. The application may decide not to send the "ringing feedback"
     *    notification by not using this API. The `ringingFeedbackMode` configuration
     *    must be set to 'manual' to use this API.
     *
     * The specified call must be an incoming call in `Initiated` state. The call
     *    will enter `Ringing` state as a result of the operation.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. The remote participant will
     *    be notified of the operation through a
     *    {@link call.event:call:stateChange call:stateChange} event as well.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @method sendRingingFeedback
     * @param {string} callId The ID of the call.
     */
    sendRingingFeedback (callId) {
      log.debug(API_LOG_TAG + 'call.sendRingingFeedback: ', callId)

      dispatch(actions.sendRingingFeedback(callId))
    },

    /**
     * Ignores an incoming call.
     *
     * The specified call to ignore must be in a ringing state with an incoming
     *    direction. The call will be ended as a result of the operation.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. The remote participant
     *    will not be notified that the call was ignored.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @method ignore
     * @param {string} callId The ID of the call to ignore.
     */
    ignore (callId) {
      log.debug(API_LOG_TAG + 'call.ignore: ', callId)
      dispatch(actions.ignoreCall(callId))
    },

    /**
     * Puts a call on hold.
     *
     * The specified call to hold must not already be locally held. Any/all
     *    media received from the remote participant will stop being received,
     *    and any/all media being sent to the remote participant will stop
     *    being sent.
     *
     * Some call operations cannot be performed while the call is on hold. The
     *    call can be taken off hold with the {@link call.unhold} API.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. The remote participant
     *    will be notified of the operation through a
     *    {@link call.event:call:stateChange call:stateChange} event as well.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @method hold
     * @param {string} callId The ID of the call to hold.
     */
    hold (callId) {
      log.debug(API_LOG_TAG + 'call.hold: ', callId)
      dispatch(actions.holdCall(callId))
    },

    /**
     * Takes a call off hold.
     *
     * The specified call to unhold must be locally held. If the call is not
     *    also remotely held, call media will be reconnected as it was before
     *    the call was held.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. The remote participant
     *    will be notified of the operation through a
     *    {@link call.event:call:stateChange call:stateChange} event as well.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @method unhold
     * @param {string} callId The ID of the call to unhold.
     */
    unhold (callId) {
      log.debug(API_LOG_TAG + 'call.unhold: ', callId)
      dispatch(actions.unholdCall(callId))
    },

    /**
     * Set the {@link call.CustomParameter Custom Parameters} of a Call, to be provided to the remote endpoint.
     *
     * The specified parameters will be saved as part of the call's information throughout the duration of the call.
     * All subsequent call operations will include these custom parameters.
     * Therefore, invalid parameters, or parameters not previously configured on the server, will cause subsequent call operations to fail.
     *
     * A Call's custom parameters are a property of the Call's {@link call.CallObject CallObject},
     *    which can be retrieved using the {@link call.getById} or
     *    {@link call.getAll} APIs.
     *
     * The custom parameters set on a call can be sent directly with the {@link call.sendCustomParameters} API.
     *
     * Custom parameters can be removed from a call's information by setting them as undefined (e.g., `call.setCustomParameters(callId)`).
     * Subsequent call operations will no longer send custom parameters.
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @requires callMe
     * @method setCustomParameters
     * @param {string} callId The ID of the call.
     * @param {Array<call.CustomParameter>} customParameters The custom parameters to set.
     */

    /**
     * Set custom SIP headers and bodies for a call, to be provided to the remote endpoint.
     *
     * This API takes either an array of {@link call.CustomParameter Custom Parameters}, or if custom bodies
     *  also need to be set, an object consisting of either {@link call.CustomParameter Custom Parameters} or
     *  {@link call.CustomBody Custom Bodies} or both.
     *
     * Note that usage of the `setCustomParameters` API will completely overwrite any previously set headers and bodies.
     *  Therefore, if either parameters or headers are omitted during subsequent API calls, they will
     *  be overwritten to undefined.
     * @private
     * @static
     * @memberof call
     * @requires link_call
     * @method setCustomParameters
     * @param {Array<call.CustomParameter> | Object} customParameters The custom parameters to set.
     * @param {Array<call.CustomParameter>} [customParameters.customParameters] Custom SIP headers for the SIP backend.
     * @param {Array<call.CustomBody>} [customParameters.customBodies] Custom SIP bodies for the SIP backend.
     */
    setCustomParameters (callId, customParameters) {
      log.debug(API_LOG_TAG + 'call.setCustomParameters: ', callId, customParameters)
      dispatch(actions.setCustomParameters(callId, customParameters))
    },

    /**
     * Send the custom parameters on an ongoing call to the server. The server may either consume the headers or relay them
     * to another endpoint, depending on how the server is configured.
     *
     * A Call's custom parameters are a property of the Call's {@link call.CallObject CallObject},
     *    which can be retrieved using the {@link call.getById} or
     *    {@link call.getAll} APIs.
     *
     * To change or remove the custom parameters on a call, use the {@link call.setCustomParameters} API.
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @requires callMe
     * @method sendCustomParameters
     * @param {string} callId The ID of the call being acted on.
     */
    sendCustomParameters (callId) {
      log.debug(API_LOG_TAG + 'call.sendCustomParameters: ', callId)
      dispatch(actions.sendCustomParameters(callId))
    },

    /**
     * Retrieves the information of all calls made during the current session.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @method getAll
     * @return {Array<call.CallObject>} Call objects.
     * @example
     * let calls = client.call.getAll()
     * let currentCalls = calls.filter(call => {
     *     return call.state === client.call.states.CONNECTED
     * })
     */
    getAll () {
      log.debug(API_LOG_TAG + 'call.getAll')
      return getCalls(getState())
    },

    /**
     * Retrieves the information of a single call with a specific call ID.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @static
     * @method getById
     * @param {string} callId The ID of the call to retrieve.
     * @return {call.CallObject} A call object.
     */
    getById (callId) {
      log.debug(API_LOG_TAG + 'call.getById: ', callId)
      return getCallById(getState(), callId)
    },

    /**
     * Ends an ongoing call.
     *
     * The SDK will stop any/all local media associated with the call. Events
     *    will be emitted to indicate which media tracks were stopped. See the
     *    {@link call.event:call:trackEnded call:trackEnded} event for more
     *    information.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event locally when the operation completes. The remote participant
     *    will be notified, through their own
     *    {@link call.event:call:stateChange call:stateChange} event, that the
     *    call was ended.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @method end
     * @param {string} callId The ID of the call to end.
     */
    end (callId) {
      log.debug(API_LOG_TAG + 'call.end: ', callId)
      dispatch(actions.endCall(callId))
    },

    /**
     * Add new media tracks to an ongoing call.
     * Will get new media tracks from the specific sources to add to the call.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:newTrack call:newTrack} event
     *    both for the local and remote users to indicate a track has been
     *    added to the Call.
     *
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {string}  callId The ID of the call to add media to.
     * @param {Object}  [media={}] The media options to add to the call.
     * @param {boolean} [media.audio=false] Whether to add audio to the call.
     * @param {boolean} [media.video=false] Whether to add video to the call.
     * @param {boolean} [media.screen=false] Whether to add the screenshare to the call. (Note: Screensharing is not supported on iOS Safari.)
     * @param {Object}  [media.audioOptions] Options for configuring the call's audio.
     * @param {Object}  [media.videoOptions] Options for configuring the call's video.
     * @param {Object}  [media.screenOptions] Options for configuring the call's screenShare.
     * @param {call.MediaConstraint} [media.audioOptions.deviceId] ID of the microphone to receive audio from.
     * @param {call.MediaConstraint} [media.videoOptions.deviceId] ID of the camera to receive video from.
     * @param {call.MediaConstraint} [media.videoOptions.height] The height of the video.
     * @param {call.MediaConstraint} [media.videoOptions.width] The width of the video.
     * @param {call.MediaConstraint} [media.videoOptions.frameRate] The frame rate of the video.
     * @param {call.MediaConstraint} [media.screenOptions.height] The height of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.width] The width of the screenShare.
     * @param {call.MediaConstraint} [media.screenOptions.frameRate] The frame rate of the screenShare.
     * @param {Object} [options]
     * @param {call.BandwidthControls} [options.bandwidth] Options for configuring media's bandwidth.
     * @param {call.DSCPControls} [options.dscpControls] Options for configuring DSCP markings on the media traffic
     */
    addMedia (callId, media = {}, options = {}) {
      log.debug(API_LOG_TAG + 'call.addMedia: ', callId, media)
      const mediaConstraints = {
        audio: media.audio && !isEmpty(media.audioOptions) ? media.audioOptions : media.audio,
        video: media.video && !isEmpty(media.videoOptions) ? media.videoOptions : media.video,
        screenShare: media.screen && !isEmpty(media.screenOptions) ? media.screenOptions : media.screen
      }

      dispatch(actions.addMedia(callId, { mediaConstraints, ...options }))
    },

    /**
     * Remove tracks from an ongoing call.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:trackEnded call:trackEnded}
     *    event for both the local and remote users to indicate that a track
     *    has been removed.
     *
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {string} callId The ID of the call to remove media from.
     * @param {Array} tracks A list of track IDs to remove.
     * @param {Object} [options]
     * @param {call.BandwidthControls} [options.bandwidth] Options for configuring media's bandwidth.
     */
    removeMedia (callId, tracks, options = {}) {
      log.debug(API_LOG_TAG + 'call.removeMedia: ', callId, tracks)
      dispatch(actions.removeMedia(callId, { tracks, ...options }))
    },

    /**
     * Adds local video to an ongoing Call, to start sending to the remote
     *    participant.
     *
     * Can only be used in a basic media scenario, where the Call does not
     *    already have video. For more advanced scenarios, the
     *    {@link call.addMedia} API can be used.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:newTrack call:newTrack} event
     *    both for the local and remote users to indicate a track has been
     *    added to the Call.
     *
     * @public
     * @static
     * @method startVideo
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {string}                 callId              ID of the call being acted on.
     * @param {Object}                 [videoOptions]           Options for configuring the call's video.
     * @param {call.MediaConstraint}   [videoOptions.deviceId]  ID of the camera to receive video from.
     * @param {call.MediaConstraint}   [videoOptions.height]    The height of the video.
     * @param {call.MediaConstraint}   [videoOptions.width]     The width of the video.
     * @param {call.MediaConstraint}   [videoOptions.frameRate] The frame rate of the video.
     * @param {Object}                 [options]
     * @param {call.BandwidthControls} [options.bandwidth] Options for configuring media's bandwidth.
     * @param {call.DSCPControls}      [options.dscpControls] Options for configuring DSCP markings on the media traffic.
     */
    startVideo (callId, videoOptions = {}, options = {}) {
      log.debug(API_LOG_TAG + 'call.startVideo: ', callId, options)

      const mediaConstraints = {
        audio: false,
        video: !isEmpty(videoOptions) ? videoOptions : true,
        screenShare: false
      }
      dispatch(actions.addBasicMedia(callId, { mediaConstraints, kind: 'video', ...options }))
    },

    /**
     * Removes local video from an ongoing Call, stopping it from being sent
     *    to the remote participant.
     *
     * Can only be used in a basic media scenario, where the Call has only one
     *    video track. For more advanced scenarios, the
     *    {@link call.removeMedia} API can be used.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:trackEnded call:trackEnded}
     *    event for both the local and remote users to indicate that a track
     *    has been removed.
     *
     * @public
     * @static
     * @method stopVideo
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {string} callId ID of the call being acted on.
     */
    stopVideo (callId) {
      log.debug(API_LOG_TAG + 'call.stopVideo: ', callId)
      dispatch(actions.removeBasicMedia(callId, { kind: 'video' }))
    },

    /**
     * Adds local screenshare to an ongoing Call, to start sending to the remote
     *    participant.
     *
     * The latest SDK release (v4.X+) has not yet implemented this API in the
     *    same way that it was available in previous releases (v3.X). In place
     *    of this API, the SDK has a more general API that can be used for this
     *    same behaviour.
     *
     * The {@link call.addMedia} API can be used to perform the same behaviour
     *    as `startScreenshare`. {@link call.addMedia} is a general-purpose API
     *    for adding media to a call, which covers the same functionality as
     *    `startScreenshare`. Selecting only screen options when using
     *    {@link call.addMedia} will perform the same behaviour as using
     *    `startScreenshare`.
     *
     * @public
     * @static
     * @method startScreenshare
     * @memberof call
     * @requires call
     * @requires callMe
     * @example
     * // Select media options for adding only screenshare.
     * const media = {
     *    audio: false,
     *    video: false,
     *    screen: true,
     *    screenOptions: { ... }
     * }
     *
     * // Add the selected media to the call.
     * client.call.addMedia(callId, media)
     */

    /**
     * Removes local screenshare from an ongoing Call, stopping it from being
     *    sent to the remote participant.
     *
     * The latest SDK release (v4.X+) has not yet implemented this API in the
     *    same way that it was available in previous releases (v3.X). In place
     *    of this API, the SDK has a more general API that can be used for this
     *    same behaviour.
     *
     * The {@link call.removeMedia} API can be used to perform the same
     *    behaviour as `stopScreenshare`. {@link call.removeMedia} is a
     *    general-purpose API for removing media from a call, which covers the
     *    same functionality as `stopScreenshare`. Specifying only the screen
     *    track when using {@link call.removeMedia} will perform the same
     *    behaviour as using `stopScreenshare`.
     *
     * There is a caveat that if a Call has multiple video tracks (for example,
     *    both a video and a screen track), the SDK itself cannot yet
     *    differentiate one from the other. The application will need to know
     *    which track was the screen track in this scenario.
     * @public
     * @static
     * @method stopScreenshare
     * @memberof call
     * @requires call
     * @requires callMe
     * @example
     * const call = client.call.getById(callId)
     * // Get the ID of any/all video tracks on the call.
     * const videoTracks = call.localTracks.filter(trackId => {
     *    const track = call.media.getTrackById(trackId)
     *    // Both video and screen tracks have kind of 'video'.
     *    return track.kind === 'video'
     * })
     *
     * // Pick out the screen track.
     * const screenTrack = videoTracks[0]
     *
     * // Remove screen from the call.
     * client.call.removeMedia(callId, [ screenTrack ])
     */

    /**
     * Send DTMF tones to a call's audio.
     *
     * The provided tone can either be a single DTMF tone (eg. '1') or a
     *    sequence of DTMF tones (eg. '123') which will be played one after the
     *    other.
     *
     * The specified call must be either in Connected, Ringing, or Early Media
     *    state, otherwise invoking this API will have no effect.
     *
     * The tones will be sent as out-of-band tones if supported by the call,
     *    otherwise they will be added in-band to the call's audio.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @method sendDTMF
     * @param {string} callId ID of the call being acted on.
     * @param {string} tone DTMF tone(s) to send. Valid characters are ['0','1','2','3','4','5','6','7','8','9','#','*' and ','].
     * @param {number} [duration=100] The amount of time, in milliseconds, that each DTMF tone should last.
     * @param {number} [intertoneGap=70] The length of time, in milliseconds, to wait between tones.
     *
     */
    sendDTMF (callId, tone, duration = 100, intertoneGap = 70) {
      log.debug(API_LOG_TAG + 'call.sendDTMF: ', callId, tone, duration, intertoneGap)
      dispatch(actions.sendDTMF(callId, { tone, duration, intertoneGap }))
    },

    /**
     * Retrieve a snapshot of the low-level information of the Call through statistical
     *    report.
     *
     * The data retrieved is a [RTCStatsReport](https://developer.mozilla.org/en-US/docs/Web/API/RTCStatsReport)
     *    object, which contains many individual
     *    [RTCStats](https://developer.mozilla.org/en-US/docs/Web/API/RTCStats).
     *    These are advanced statistics gathered by the browser providing insights
     *    into the Call at a certain point in time. Aggregating reports over a
     *    period of time would allow a low-level analysis of the Call for that
     *    period. As an example, this could be done to determine the media quality
     *    during the Call.
     *
     * A Track ID can optionally be provided to get a report for a specific
     *    Track of the Call.
     *
     * This API will return a promise which, when resolved, will contain the report of the particular call.
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a
     *    {@link call.event:call:statsReceived call:statsReceived} event, after
     *    the operation completes, that has the report.
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {string} callId The ID of the Call to retrieve the report.
     * @param {string} [trackId] ID of a Track being used by the Call. If not
     *    provided, RTCStatsReport is generated for the Call itself.
     * @example
     * client.on('call:statsReceived', function (params) {
     *    // Iterate over each individual statistic inside the RTCPStatsReport.
     *    params.result.forEach(stats => {
     *        // Handle the data on its own or collate with previously gathered stats
     *        //    for analysis.
     *        ...
     *    })
     * })
     *
     * // Get a snapshot of the Call's stats.
     * //   This may be done on a regular interval to collect data over time.
     * client.call.getStats(callId)
     */
    getStats (callId, trackId) {
      log.debug(API_LOG_TAG + 'call.getStats: ', callId, trackId)
      const deferredResult = defer()
      dispatch(actions.getStats(callId, { trackId }, deferredResult))
      return deferredResult.promise
    },

    /**
     * Forwards an incoming call to another user.
     *
     * The specified destination will receive the Call instead of the current
     *    user.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event after the operation completes.
     *
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @method forward
     * @param {string} callId ID of the call being acted on.
     * @param {call.SIP_URI|call.TEL_URI} destination The destination to forward the call to.
     */

    /**
     * Forwards an incoming call to another user.
     *
     * The specified destination will receive the Call instead of the current
     *    user.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a {@link call.event:call:stateChange call:stateChange}
     *    event after the operation completes.
     * @private
     * @static
     * @memberof call
     * @requires cpaas_call
     * @method forward
     * @param {string} callId ID of the call being acted on.
     * @param {call.UserID|call.PhoneNumber} destination The destination to forward the call to.
     */
    forward (callId, destination) {
      log.debug(API_LOG_TAG + 'call.forward: ', destination)

      const config = getOptions(getState())
      if (config.normalizeDestination) {
        // Normalize callee addresses
        destination = normalizeSipUri(destination, getDomain(getState()))
        log.info('Address normalized to: ', destination)
      }

      dispatch(actions.forwardCall(callId, { destination }))
    },

    /**
     * Performs a "consultative" transfer between two ongoing calls (also known
     *    as an announced or warm transfer). This allows the current user to
     *    transfer the remote participant of a call to another user, after
     *    having spoken to both users.
     *
     * Both calls used for the transfer must be locally held. After the
     *    operation, these calls will be ended, as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event.
     *
     * Both remote participants will see their call be unheld by the operation,
     *    as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event, and will
     *    be connected to one another afterwards.
     *
     * The progression of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event. Both local
     *    calls will receive this event, since it is an operation on both calls,
     *    and the remote calls will receive it as if it were a "remote unhold"
     *    operation.
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @method consultativeTransfer
     * @param {string} callId ID of the call being acted on.
     * @param {string} otherCallId ID of the other call being acted on.
     */

    /**
     * Performs a "consultative" transfer between two ongoing calls (also known
     *    as an announced or warm transfer). This allows the current user to
     *    transfer the remote participant of a call to another user, after
     *    having spoken to both users.
     *
     * Both calls used for the transfer must be locally held. After the
     *    operation, these calls will be ended, as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event.
     *
     * Both remote participants will see their call be unheld by the operation,
     *    as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event, and will
     *    be connected to one another afterwards.
     *
     * The progression of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event. Both local
     *    calls will receive this event, since it is an operation on both calls,
     *    and the remote calls will receive it as if it were a "remote unhold"
     *    operation.
     * @private
     * @static
     * @memberof call
     * @requires cpaas_call
     * @method consultativeTransfer
     * @param {string} callId ID of the call being acted on.
     * @param {string} otherCallId ID of the other call being acted on.
     */
    consultativeTransfer (callId, otherCallId) {
      log.debug(API_LOG_TAG + 'call.consultativeTransfer: ', callId, otherCallId)

      dispatch(actions.consultativeTransfer(callId, { otherCallId }))
    },

    /**
     * Performs a "direct" transfer on a call (also known as an unannounced or
     *    blind transfer). This allows the current user to transfer the remote
     *    participant of a call to another user, similar to a "forward"
     *    operation.
     *
     * The specified call must be locally held. After the operation, this call
     *    will be ended, as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event.
     *
     * The "destination" user will receive an incoming call, and when answered,
     *    they will be connected with the remote participant of the specified
     *    call.
     *
     * The progression of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event. The remote
     *    participant being transferred will receive it as if it were a "remote
     *    unhold" operation.
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @method directTransfer
     * @param {string} callId ID of the call being acted on.
     * @param {call.SIP_URI|call.TEL_URI} destination The destination to transfer the call to.
     */

    /**
     * Performs a "direct" transfer on a call (also known as an unannounced or
     *    blind transfer). This allows the current user to transfer the remote
     *    participant of a call to another user, similar to a "forward"
     *    operation.
     *
     * The specified call must be locally held. After the operation, this call
     *    will be ended, as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event.
     *
     * The "destination" user will receive an incoming call, and when answered,
     *    they will be connected with the remote participant of the specified
     *    call.
     *
     * The progression of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event. The remote
     *    participant being transferred will receive it as if it were a "remote
     *    unhold" operation.
     * @private
     * @static
     * @memberof call
     * @requires cpaas_call
     * @method directTransfer
     * @param {string} callId ID of the call being acted on.
     * @param {call.UserID|call.PhoneNumber} destination The destination to transfer the call to.
     */
    directTransfer (callId, destination) {
      log.debug(API_LOG_TAG + 'call.directTransfer: ', callId, destination)

      const config = getOptions(getState())
      if (config.normalizeDestination) {
        // Normalize callee addresses
        destination = normalizeSipUri(destination, getDomain(getState()))
        log.info('Address normalized to: ', destination)
      }

      dispatch(actions.directTransfer(callId, { destination }))
    },

    /**
     * Performs a "join" on two ongoing calls.
     * This allows the current user to establish a call with audio with two
     *    remote users.
     *
     * Both specified calls must be locally held. The new, "joined" call will be
     *    audio-only, even if either previous call had video. Video cannot be
     *    added to the "joined" call. Both remote participants will see their
     *    call taken off hold, and will receive additional audio from other
     *    participants after the operation. Both previous calls for the current
     *    user will be ended after the operation, as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event.
     *
     * If the first call specified has custom parameters set, these parameters will be carried over to the new call.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event. Both remote
     *    participants will also receive this event as if it were a "remote
     *    unhold" operation.
     * @public
     * @static
     * @memberof call
     * @requires link_call
     * @method join
     * @param {string} callId ID of the call being acted on.
     * @param {string} otherCallId ID of the other call being acted on.
     */

    /**
     * Performs a "join" on two ongoing calls.
     * This allows the current user to establish a call with audio with two
     *    remote users.
     *
     * Both specified calls must be locally held. The new, "joined" call will be
     *    audio-only, even if either previous call had video. Video cannot be
     *    added to the "joined" call. Both remote participants will see their
     *    call taken off hold, and will receive additional audio from other
     *    participants after the operation. Both previous calls for the current
     *    user will be ended after the operation, as indicated by a
     *    {@link call.event:call:stateChange call:stateChange} event.
     *
     * If the first call specified has custom parameters set, these parameters will be carried over to the new call.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event. Both remote
     *    participants will also receive this event as if it were a "remote
     *    unhold" operation.
     * @private
     * @static
     * @memberof call
     * @requires cpaas_call
     * @method join
     * @param {string} callId ID of the call being acted on.
     * @param {string} otherCallId ID of the other call being acted on.
     */
    join (callId, otherCallId) {
      log.debug(API_LOG_TAG + 'call.join: ', callId, otherCallId)

      const newCallId = uuidV4()
      const from = getUserInfo(getState()).username
      dispatch(actions.join(callId, { otherCallId, newCallId, from }))
    },

    /**
     * Replace a call's track with a new track of the same media type.
     *
     * The operation will remove the old track from the call and add a
     *    new track to the call. This effectively allows for changing the
     *    track constraints (eg. device used) for an ongoing call.
     *
     * Because it completely replaces an old track with a new one,
     * the old track's state characteristics are not carried over in the new track's state.
     * (e.g. if an old track's state was 'muted' and replacement of this track has occured,
     * the new track's state will be 'unmuted', as this is its default state)
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     *
     * The SDK will emit a
     *    {@link call.event:call:trackReplaced call:trackReplaced} event
     *    locally when the operation completes. The newly added track will need
     *    to be handled by the local application. The track will be replaced
     *    seamlessly for the remote application, which will not receive an event.
     *
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {string} callId The ID of the call to replace the track of.
     * @param {string} trackId The ID of the track to replace.
     * @param {Object} [media={}] The media options.
     * @param {boolean} [media.audio=false] Whether to create an audio track.
     * @param {Object} [media.audioOptions] Options for configuring the audio track.
     * @param {call.MediaConstraint} [media.audioOptions.deviceId] ID of the microphone to receive audio from.
     * @param {boolean} [media.video=false] Whether to create a video track.
     * @param {Object} [media.videoOptions] Options for configuring the video track.
     * @param {call.MediaConstraint} [media.videoOptions.deviceId] ID of the camera to receive video from.
     * @param {call.MediaConstraint} [media.videoOptions.height] The height of the video.
     * @param {call.MediaConstraint} [media.videoOptions.width] The width of the video.
     * @param {call.MediaConstraint} [media.videoOptions.frameRate] The frame rate of the video.
     * @example
     * const callId = ...
     * // Get the video track used by the call.
     * const videoTrack = ...
     *
     * // Replace the specified video track of the call with a new
     * //    video track.
     * client.call.replaceTrack(callId, videoTrack.id, {
     *   // The track should be replaced with a video track using
     *   //    a specific device. This effectively changes the input
     *   //    device for an ongoing call.
     *   video: true,
     *   videoOptions: {
     *     deviceId: cameraId
     *   }
     * })
     *
     * @example
     * const callId = ...
     * // Get the video track used by the call.
     * const videoTrack = ...
     *
     * // Can also replace the specified video track of the call with a new
     * // screen sharing track because screen sharing is delivered as a video stream to remote peer.
     * // User will then be prompted to pick a specific screen to share and thus the device id will be selected.
     * client.call.replaceTrack(callId, videoTrack.id, {
     *   // The track should be replaced with a screen sharing track.
     *   // Note that 'screenOptions' property is not mandatory, as API will use default values
     *   // for properties like: width, height, frameRate.
     *   screen: true
     * })
     */
    replaceTrack (callId, trackId, media) {
      log.debug(API_LOG_TAG + 'call.replaceTrack: ', callId, trackId, media)

      const mediaConstraints = {
        audio: media.audio && !isEmpty(media.audioOptions) ? media.audioOptions : media.audio,
        video: media.video && !isEmpty(media.videoOptions) ? media.videoOptions : media.video,
        screenShare: media.screen && !isEmpty(media.screenOptions) ? media.screenOptions : media.screen
      }
      dispatch(actions.replaceTrack(callId, { trackId, mediaConstraints }))
    },

    /**
     * Retrieve the list of available and supported codecs based on the browser's capabilities for sending media.
     *
     * This API will return a promise which, when resolved, it will contain the list of available and supported codecs.
     * In addition, the SDK emits a {@link call.event:call:availableCodecs call:availableCodecs} event
     *  upon retrieving that list of codecs.
     *
     * This API is a wrapper for the static method {@link https://w3c.github.io/webrtc-pc/#dom-rtcrtpsender-getcapabilities RTCRtpSender.getCapabilities()}.
     *  Firefox browser does not currently support this method. Therefore, this API will not work on Firefox.
     *
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {string} kind The kind of media, i.e., 'audio' or 'video', to get the list of available codecs of.
     */
    getAvailableCodecs (kind) {
      log.debug(`${API_LOG_TAG}call.getAvailableCodecs, kind: ${kind}`)

      const deferredResult = defer()

      // Trigger the action, but include a deferred result which caller can use when
      // we actually obtain the data (i.e. list of codecs) from WebRTCManager.
      dispatch(actions.getAvailableCodecs({ kind }, deferredResult))

      return deferredResult.promise
    },

    /**
     * Set {@link call.SdpHandlerFunction SDP Handler Functions} that will be run as part of a pipeline for all future calls.
     *  This will replace any SDP Handlers that were previously set.
     *
     * SDP handlers can be used to make modifications to the SDP (e.g., removing certain codecs)
     *  before they are processed or sent to the other side.
     *
     * This is an advanced feature, changing the SDP handlers mid-call may cause
     *  unexpected behaviour in future call operations for that call.
     *
     * @public
     * @static
     * @memberof call
     * @requires call
     * @requires callMe
     * @param {Array<call.SdpHandlerFunction>} sdpHandlers The list of SDP handler functions to modify SDP.
     */
    setSdpHandlers (sdpHandlers) {
      log.debug(`${API_LOG_TAG}call.setSdpHandlers, sdpHandlers:`, sdpHandlers)

      const config = getOptions(getState())
      const options = {
        removeH264Codecs: config.removeH264Codecs
      }
      dispatch(setSdpHandlers(sdpHandlers, options))
    },

    /**
     * Re-sync a Call with the server by fetching the status of the Call on the server.
     *
     * This may be useful to get and update a call's state after processing incoming notifications
     *    some time after they were received (e.g., waiting to process incoming call push notifications
     *    till a connection is established).
     *
     * The progress of the `RESYNC` operation will be tracked via the
     *    {@link call.event:call:operation call:operation} events.
     *
     * The SDK may emit a {@link call.event:call:operation call:operation} event after the `RESYNC` operation is
     *    complete if the call was ended do to the resync.
     * @private
     * @static
     * @memberof call
     * @requires call
     * @requires link_call
     * @method resync
     * @param {string} callId The ID of the call to re-sync.
     */
    resync (callId) {
      log.debug(`${API_LOG_TAG}call.resync, callId: ${callId}`)
      dispatch(actions.resync(callId))
    },

    /**
     * Possible states that a Call can be in.
     *
     * A Call's state describes the current status of the Call. An application
     *    should use the state to understand how the Call, and any media
     *    associated with the Call, should be handled. Which state the Call is
     *    in defines how it can be interacted with, as certain operations can
     *    only be performed while in specific states, and tells an application
     *    whether the Call currently has media flowing between users.
     * Unless stated otherwise, the Call's state pertains to both caller & callee.
     *
     * The Call's state is a property of the {@link call.CallObject CallObject},
     *    which can be retrieved using the {@link call.getById} or
     *    {@link call.getAll} APIs.
     *
     * The SDK emits a {@link call.event:call:stateChange call:stateChange}
     *    event when a Call's state changes from one state to another.
     *
     * @public
     * @static
     * @memberof call
     * @requires callMe
     * @requires call
     * @type {Object}
     * @property {string} INITIATING The (outgoing) call is being started. While in this state, no Call operations can be performed until Call gets into Initiated state.
     * @property {string} INITIATED A call has been started and both the callee and caller may now perform further operations on the call object.
     * @property {string} RINGING The call has been received by both parties, and is waiting to be answered.
     * @property {string} EARLY_MEDIA The call has not been answered, but media
     *    is already being received. This may be network-ringing media, IVR
     *    system media, or other pre-answer medias. When early media is
     *    supported, this state may replace the Ringing state. This is a state valid only for caller's side.
     * @property {string} CANCELLED The call was disconnected before it could be answered. This is a state valid only for callee's side.
     * @property {string} CONNECTED Both parties are connected and media is flowing.
     * @property {string} ON_HOLD Both parties are connected but no media is flowing.
     * @property {string} ENDED The call has ended.
     * @example
     * // Use the call states to know how to handle a change in the call.
     * client.on('call:stateChange', function (params) {
     *    const call = client.call.getById(params.callId)
     *    // Check if the call now has media flowing.
     *    if (call.state === client.call.states.CONNECTED) {
     *      // The call is now active, and can perform midcall operations.
     *    }
     * })
     */
    states: CALL_STATES,

    /**
     * Possible states that a Call's media connection can be in.
     *
     * A Call's media connection state describes the current status of media within the call.
     *  An application should use this state to understand whether the Call participants are
     *  able to see/hear each other or may be experiencing connection issues. The media connection
     *  state can be used alongside the {@link call.states Call state} to determine if media issues
     *  are occurring while the participants are expecting to be connected.
     *
     * An important state to check for is the `FAILED` state. This state signifies that there is no
     *    media connection between the call participants and an action must be taken to resolve the
     *    problem. Using the {@link call.restartMedia} API will attempt to reconnect the media. See
     *    the {@link call.restartMedia} API description for more information.
     *
     * These states are direct reflections of the possible
     *  [RTCPeerConnection.iceConnectionState](https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/iceConnectionState) values.
     *
     * The Call's media connection state is a property of the {@link call.CallObject CallObject},
     *    which can be retrieved using the {@link call.getById} or
     *    {@link call.getAll} APIs.
     *
     * The SDK emits a {@link call.event:call:mediaConnectionChange call:mediaConnectionChange}
     *    event when a Call's media connection state changes from one state to another.
     *
     * @public
     * @static
     * @memberof call
     * @requires callMe
     * @requires call
     * @type {Object}
     * @property {string} NEW The Call is initializing the local side of the connection and waiting on information from the remote side.
     *  When the information is received, the state will transition into `checking` as the Call automatically begins the connection process.
     * @property {string} CHECKING The Call has received information from the remote endpoint and is working to establish the media connection.
     *  When finished, the state will transition to either `connected` or `failed`.
     * @property {string} CONNECTED A usable connection has been made and the Call will now have media.
     *  The connection may not be optimal, though, so the Call will continue establishment to improve the connection before going into the `completed` state.
     * @property {string} COMPLETED The media connection process has fully completed and the optimal connection has been established. While in this state,
     *  the Call endpoints will receive each other's media.
     * @property {string} DISCONNECTED Media has become disconnected and the Call endpoints have stopped receiving each other's media.
     *  The Call will automatically attempt to reconnect, transitioning back to `completed` if successful or to `failed` if not.
     * @property {string} FAILED The connection has failed and cannot be recovered automatically. A full media connection refresh is required to reestablish a connection. See the {@link call.restartMedia} API.
     * @property {string} CLOSED The connection has been shut down and is no longer in use.
     * @example
     * // Use the media connection states to check the status of the media connection of the Call.
     * client.on('call:mediaConnectionChange', function (params) {
     *   // Retrieve the state of the Call this event is for.
     *   const call = client.call.getById(params.callId)
     *   const mediaConnectionStates = client.call.mediaConnectionStates
     *
     *   // Check the mediaConnectionState to determine which scenario the call is in.
     *   switch (call.mediaConnectionState) {
     *     case mediaConnectionStates.CONNECTED:
     *     case mediaConnectionStates.COMPLETED:
     *       // Media established: The Call's media is connected. The Call's participants
     *       //    are able to see/hear each other.
     *       // These states will occur after Call establishment.
     *       ...
     *       break
     *     case mediaConnectionStates.NEW:
     *     case mediaConnectionStates.CHECKING:
     *     case mediaConnectionStates.DISCONNECTED:
     *       // Media pending: The Call's media is not connected. The Call is working
     *       //    to connect media automatically.
     *       // These states will occur during Call establishment and may occur midcall if there are
     *       //    connection issues (eg. poor network quality) or a Call participant has changed (eg. transfer).
     *       ...
     *       break
     *     case mediaConnectionStates.FAILED:
     *      // Media has failed. The call requires a media refresh to reestablish.
     *      // This state will occur after the `DISCONNECTED` state is encountered.
     *      ...
     *       break
     *     case mediaConnectionStates.CLOSED:
     *       // Media ended due to the Call being ended.
     *       // This state will occur after Call establishment.
     *       ...
     *       break
     *   }
     * }
     */
    mediaConnectionStates: CALL_MEDIA_CONNECTION_STATES,

    /**
     * Attempt to re-establish a media connection for a call.
     *
     * This API will perform a "refresh" operation on the call with the intention
     *    of resolving media issues that may have been encountered. This API is only
     *    necessary after the Call's {@link call.mediaConnectionStates mediaConnectionState}
     *    has entered the `failed` state, but may be used in other scenarios.
     *
     * After the operation completes successfully, the Call will be re-establishing
     *    its media connection. By this time, or shortly after, the Call's
     *    {@link call.mediaConnectionStates mediaConnectionState} should have
     *    transitioned to `checking` (via a
     *    {@link call.event:call:mediaConnectionChange call:mediaConnectionChange}
     *    event) to signify the re-establishment. It will then transition to either
     *    `connected` or `failed` state, similar to during the initial Call establishment.
     *
     * If this operation fails, then the Call will not attempt the re-establishment
     *    and will remain in the `failed` {@link call.mediaConnectionStates mediaConnectionState}.
     *
     * Behaviour during the operation may differ slightly based on the browser.
     *    Notably, Firefox will always transition to the `checking`
     *    {@link call.mediaConnectionStates mediaConnectionState} no matter what
     *    the previous state was. Whereas Chrome will skip the `checking` state,
     *    transitioning directly to either `connected` or `failed`. This has the
     *    implication for Chrome that if the state does not change (for example,
     *    the Call is in the `failed` state before the media restart operation,
     *    and media re-establishment fails), then there will be no
     *    {@link call.event:call:mediaConnectionChange call:mediaConnectionChange}
     *    event emitted. For this reason, Chrome-based applications may need a
     *    short delay after receiving the {@link call.event:call:mediaRestart call:mediaRestart}
     *    event before checking the Call's updated
     *    {@link call.mediaConnectionStates mediaConnectionState} to ensure the
     *    application is acting on the "latest" state.
     *
     * The SDK will emit a {@link call.event:call:mediaRestart call:mediaRestart}
     *    event when the operation completes.
     *
     * The progress of the operation will be tracked via the
     *    {@link call.event:call:operation call:operation} event.
     * @public
     * @static
     * @memberof call
     * @method restartMedia
     * @param {string} callId The ID of the call to act on.
     */
    restartMedia (callId) {
      log.debug(`${API_LOG_TAG}call.restartMedia, callId: ${callId}`)
      dispatch(actions.restartMedia(callId))
    }

    /**
     * The `setDefaultDevices` API from previous SDK releases (3.X) has been
     *    deprecated in the latest releases (4.X+). The SDK no longer keeps
     *    track of "default devices" on behalf of the application.
     *
     * The devices used for a call can be selected as part of the APIs for
     *    starting the call. Microphone and/or camera can be chosen in the
     *    {@link call.make} and {@link call.answer} APIs, and speaker can be
     *    chosen when the audio track is rendered with the
     *    {@link media.renderTracks} API.
     * @public
     * @static
     * @method setDefaultDevices
     * @memberof call
     * @requires call
     * @requires callMe
     */

    /**
     * Changes the camera and/or microphone used for a Call's media input.
     *
     * The latest SDK release (v4.X+) has not yet implemented this API in the
     *    same way that it was available in previous releases (v3.X). In place
     *    of this API, the SDK has a more general API that can be used for this
     *    same behaviour.
     *
     * The same behaviour as the `changeInputDevices` API can be implemented
     *    using the general-purpose {@link call.replaceTrack} API. This API can
     *    be used to replace an existing media track with a new track of the
     *    same type, allowing an application to change certain aspects of the
     *    media, such as input device.
     * @public
     * @static
     * @method changeInputDevices
     * @memberof call
     * @requires call
     * @requires callMe
     * @example
     * const call = client.call.getById(callId)
     * // Get the ID of the Call's video track.
     * const videoTrack = call.localTracks.find(trackId => {
     *    const track = client.media.getTrackById(trackId)
     *    return track.kind === 'video'
     * })
     *
     * // Select the new video options.
     * const media = {
     *    video: true,
     *    videoOptions: {
     *        deviceId: 'cameraId'
     *    }
     * }
     *
     * // Change the call's camera by replacing the video track.
     * client.call.replaceTrack(callId, videoTrack, media)
     */

    /**
     * Changes the speaker used for a Call's audio output. Supported on
     *    browser's that support HTMLMediaElement.setSinkId().
     *
     * The latest SDK release (v4.X+) has not yet implemented this API in the
     *    same way that it was available in previous releases (v3.X). In place
     *    of this API, the SDK has a more general API that can be used for this
     *    same behaviour.
     *
     * The same behaviour as the `changeSpeaker` API can be implemented by
     *    re-rendering the Call's audio track.  A speaker can be selected when
     *    rendering an audio track, so changing a speaker can be simulated
     *    by unrendering the track with {@link media.removeTracks}, then
     *    re-rendering it with a new speaker with {@link media.renderTracks}.
     * @public
     * @static
     * @method changeSpeaker
     * @memberof call
     * @requires call
     * @requires callMe
     * @example
     * const call = client.call.getById(callId)
     * // Get the ID of the Call's audio track.
     * const audioTrack = call.localTracks.find(trackId => {
     *    const track = client.media.getTrackById(trackId)
     *    return track.kind === 'audio'
     * })
     *
     * // Where the audio track was previously rendered.
     * const audioContainer = ...
     *
     * // Unrender the audio track we want to change speaker for.
     * client.media.removeTrack([ audioTrack ], audioContainer)
     * // Re-render the audio track with a new speaker.
     * client.media.renderTrack([ audioTrack ], audioContainer, {
     *    speakerId: 'speakerId'
     * })
     */
  }
}
